import React, { useState, useEffect, useCallback } from 'react';
import { Check, ChevronLeft, ChevronRight, Dices, Sparkles } from 'lucide-react';
import CombatLogManager from '../../utils/CombatLogManager';
import CombatManager from '../../utils/CombatManager';
import ActionEconomyManager from '../../utils/ActionEconomyManager';
import NotificationManager from '../../utils/NotificationManager';
import { buildInitiativeRoll, compareInitiative, initiativeLogEntry, initiativeModifierFor, isCurrentInitiativeRoll } from '../../../../shared/rules/battleRoomInitiative';
import './InitiativeTracker.css';
import { useMultiplayerSession } from '../../contexts/WebSocketContext';
import { getViewerVisibleTokens, getTokenLayerId, isTokenOnBattleLayer } from '../../utils/battleRoomLayerUtils';
import { getCombatantActionBlockReason } from '../../utils/combat/CombatantNormalizer';

/**
 * Initiative Tracker Component - Turn-Based Combat Management
 * 
 * This component manages the turn order and combat flow for D&D sessions.
 * It automatically integrates with the tokens on the battle map to provide
 * seamless combat tracking.
 * 
 * WHAT IT DOES:
 * - Shows all combatants (players, monsters, NPCs) in initiative order (highest to lowest)
 * - Displays only initiative value and name for clean, focused combat tracking
 * - Highlights whose turn it is with the BattleRoom's warm-gold active treatment
 * - Provides Next/Previous turn buttons to advance combat
 * - Tracks combat rounds automatically
 * - HP and AC are managed in Character Sheet and Token Properties panels
 * 
 * SMART BEHAVIOR:
 * - Automatically syncs with tokens placed on the battle map
 * - Only includes actual combatants (excludes map assets and objects)
 * - Updates in real-time when token properties are changed
 * - Falls back to mock data if no tokens are present
 * - Sorts combatants by initiative value automatically
 * 
 * COMBAT FLOW:
 * 1. Players/DM add character and monster tokens to the map
 * 2. Initiative Tracker automatically picks them up and sorts by initiative
 * 3. DM clicks "Next" to advance turns through combat
 * 4. Current combatant is highlighted and can take actions
 * 5. HP can be adjusted during combat as damage is taken
 * 
 * INTEGRATION:
 * - Receives tokens array from BattleRoom to populate combatant list
 * - Calls onCombatantChange when turn advances to notify other components
 * - Works with Action Panel to coordinate whose turn it is
 */

const InitiativeTracker = React.forwardRef(({
  tokens = [],
  allTokens = tokens,
  onCombatantChange,
  onTokenSelect,
  onResetCombat,
  onLocalInitiativeChange,
  // Multiplayer props - date_of_change: 2025-09-24
  connectedPlayers = [],
  currentUser = null,
  isGameMaster = false,
  tokenOwnership = {},
  getTokenControlInfo = () => ({ controlled: false, displayText: 'Unassigned', color: '#gray' }),
  canControlToken = null,
  // @date_of_change: 2025-10-05 - Added WebSocket initiative sync
  sendInitiativeUpdate = null,
  initiativeExternallyManaged = false,
  externalInitiativeState = null,
  authoritativeInitiative = null,
  combatActive = false
}, ref) => {
  const sourceTokens = React.useMemo(
    () => (Array.isArray(allTokens) && allTokens.length > 0 ? allTokens : tokens),
    [allTokens, tokens]
  );
  const viewerVisibleTokens = React.useMemo(
    () => getViewerVisibleTokens(sourceTokens, isGameMaster),
    [sourceTokens, isGameMaster]
  );
  // WHAT: GM-layer tokens stay in the authoritative turn order but never render in the tracker.
  // WHY: The GM layer is the deliberate staging boundary for unrevealed creatures; exposing their
  // names or initiatives in either role's tracker would disclose them. date_of_change: 2026-09-20
  const trackerVisibleTokens = React.useMemo(
    () => viewerVisibleTokens.filter((token) => !isTokenOnBattleLayer(token, 'gamemaster')),
    [viewerVisibleTokens]
  );
  const viewerVisibleTokenIds = React.useMemo(
    () => new Set(viewerVisibleTokens.map((token) => token.id)),
    [viewerVisibleTokens]
  );
  const trackerVisibleTokenIds = React.useMemo(
    () => new Set(trackerVisibleTokens.map((token) => token.id)),
    [trackerVisibleTokens]
  );

  // Use real tokens if available, otherwise fall back to mock data
  const [localCombatants, setCombatants] = useState(
    sourceTokens.length > 0 ? sourceTokens
      .filter(token => token.type !== 'map' && token.type !== 'object') // Exclude map assets
      .map(token => ({
        id: token.id,
        name: token.name || 'Unnamed Token',
        initiative: token.initiative ?? 10,
        initiativeRoll: token.initiativeRoll,
        type: token.type || 'player',
        hp: token.entity?.hpObj?.current ?? token.hpObj?.current ?? token.hp ?? 100,
        maxHp: token.entity?.hpObj?.max ?? token.hpObj?.max ?? token.maxHp ?? 100,
        hpObj: token.entity?.hpObj
          || token.hpObj
          || {
            current: token.hp ?? 100,
            max: token.maxHp ?? token.hp ?? 100
          },
        ac: token.ac ?? 15,
        conditions: token.conditions ?? [],
        statusEffects: token.statusEffects ?? {},
        statusEffectsMeta: token.statusEffectsMeta ?? {},
        showKoMarker: token.showKoMarker ?? false
      }))
      .sort(compareInitiative) : []
    // Don't use mock data if there are no tokens, as it causes problems with non-existent token IDs
  );

  // Mobile drawers intentionally unmount their contents. Seed from the
  // BattleRoom/manager combat snapshot so reopening a drawer cannot present a
  // second "start combat" flow or reset the active turn.
  // date_of_change: 2026-07-27
  const resumedCombat = Boolean(combatActive || CombatLogManager.combatStarted);
  const [localTurnIndex, setCurrentTurnIndex] = useState(
    resumedCombat && Number.isInteger(Number(CombatLogManager.currentTurn))
      ? Number(CombatLogManager.currentTurn)
      : 0
  );
  const [localRound, setRound] = useState(
    resumedCombat ? Math.max(1, Number(CombatLogManager.currentRound) || 1) : 1
  );
  const [locallyConfirmed, setIsInitiativeConfirmed] = useState(resumedCombat);
  const [localCombatStarted, setCombatStarted] = useState(resumedCombat);
  // Live order is a controlled view of the room, including on a drawer's
  // first render. Local and externally managed rooms retain their workflows.
  // D&D Basic Rules, Initiative. date_of_change: 2026-11-04
  const combatants = authoritativeInitiative?.combatants ?? localCombatants;
  const currentTurnIndex = authoritativeInitiative?.currentTurnIndex ?? localTurnIndex;
  const round = authoritativeInitiative?.round ?? localRound;
  const isInitiativeConfirmed = authoritativeInitiative?.isInitiativeConfirmed ?? locallyConfirmed;
  const combatStarted = authoritativeInitiative?.combatStarted ?? localCombatStarted;
  const initiativeRequestPending = React.useRef(false);

  // @date_of_change: 2025-10-14 - Multiplayer connectivity context for gating actions
  const { isConnected: wsConnected, isInSession: wsInSession } = useMultiplayerSession();
  const canBroadcast = !sendInitiativeUpdate || (wsConnected && wsInSession);
  const getCurrentCombatant = useCallback(() => (
    authoritativeInitiative && !authoritativeInitiative.activeCombatantId
      ? null : combatants[currentTurnIndex] || null
  ), [authoritativeInitiative, combatants, currentTurnIndex]);
  const visibleCombatants = React.useMemo(
    () => combatants.filter((combatant) => trackerVisibleTokenIds.has(combatant.id)),
    [combatants, trackerVisibleTokenIds]
  );
  const currentCombatant = getCurrentCombatant();

  useEffect(() => {
    if (authoritativeInitiative) return;
    if (!combatActive && !CombatLogManager.combatStarted) return;
    setIsInitiativeConfirmed(true);
    setCombatStarted(true);
    setRound(Math.max(1, Number(CombatLogManager.currentRound) || 1));
    setCurrentTurnIndex((previous) => {
      const managerTurn = Number(CombatLogManager.currentTurn);
      const requested = Number.isInteger(managerTurn) ? managerTurn : previous;
      return combatants.length > 0
        ? Math.max(0, Math.min(requested, combatants.length - 1))
        : 0;
    });
  }, [authoritativeInitiative, combatActive, combatants.length]);

  const resolveTurnControl = useCallback((combatant) => {
    if (!combatant) {
      return {
        canControl: false,
        controlInfo: { controlled: false },
        inferredControlled: false
      };
    }

    if (isGameMaster) {
      return {
        canControl: true,
        controlInfo: { controlled: true, displayText: 'Game Master' },
        inferredControlled: true
      };
    }

    // WHAT: Treat the supplied ownership predicate as authoritative, including a false result.
    // WHY: `controlInfo.controlled` can mean another participant owns the token and must not expose
    // Finish My Turn to the wrong player. date_of_change: 2026-09-20
    if (typeof canControlToken === 'function') {
      try {
        const canControl = Boolean(canControlToken(combatant.id));
        return {
          canControl,
          controlInfo: { controlled: canControl, displayText: canControl ? 'Controlled' : 'Not controlled' },
          inferredControlled: canControl
        };
      } catch (_) {
        return {
          canControl: false,
          controlInfo: { controlled: false, displayText: 'Ownership unavailable' },
          inferredControlled: false
        };
      }
    }

    let controlInfo = { controlled: false };
    try {
      controlInfo = getTokenControlInfo(combatant.id) || controlInfo;
    } catch (_) {}

    const inferredControlled = controlInfo?.displayText === 'All Players'
      || (!!controlInfo?.userId && !!currentUser?.userId && controlInfo.userId === currentUser.userId);

    return {
      canControl: inferredControlled,
      controlInfo,
      inferredControlled
    };
  }, [isGameMaster, canControlToken, getTokenControlInfo, currentUser]);

  const buildInitiativeSyncPayload = useCallback((nextCombatants, nextTurnIndex, nextRound, nextConfirmed, nextCombatStarted) => ({
    combatants: nextCombatants,
    currentTurnIndex: nextTurnIndex,
    activeCombatantId: nextCombatants?.[nextTurnIndex]?.id || null,
    round: nextRound,
    isInitiativeConfirmed: nextConfirmed,
    combatStarted: nextCombatStarted
  }), []);

  const buildCombatantFromToken = useCallback((token, existingCombatant = null) => {
    let hp = existingCombatant?.hp ?? 100;
    let maxHp = existingCombatant?.maxHp ?? 100;
    let hpObj = existingCombatant?.hpObj ?? { current: hp, max: maxHp };

    if (token?.entity?.hpObj) {
      hpObj = token.entity.hpObj;
      hp = hpObj.current;
      maxHp = hpObj.max;
    } else if (token?.hpObj) {
      hpObj = token.hpObj;
      hp = hpObj.current;
      maxHp = hpObj.max;
    } else {
      hp = token?.hp ?? existingCombatant?.hp ?? 100;
      maxHp = token?.maxHp ?? existingCombatant?.maxHp ?? 100;
      hpObj = { current: hp, max: maxHp };
    }

    // GAP-006: Resolve concentration spell name from statusEffectsMeta or concentrationSpell field.
    // rule_reference: D&D 5e 2024 — only one concentration spell at a time per creature.
    // date_of_change: 2026-05-20
    const resolvedStatusEffectsMeta = token?.statusEffectsMeta ?? existingCombatant?.statusEffectsMeta ?? {};
    let resolvedConcentrationSpell = token?.concentrationSpell ?? existingCombatant?.concentrationSpell ?? null;
    if (!resolvedConcentrationSpell) {
      // Scan statusEffectsMeta for any entry with concentration:true to surface the spell name
      const concEntry = Object.values(resolvedStatusEffectsMeta).find(m => m && m.concentration === true && m.description);
      if (concEntry) {
        // Extract spell name from description (strip trailing parenthetical notes)
        resolvedConcentrationSpell = String(concEntry.description || '').replace(/\s*\(.*?\).*$/, '').trim() || null;
      }
    }

    return {
      id: token?.id ?? existingCombatant?.id,
      name: token?.name || existingCombatant?.name || 'Unnamed Token',
      initiative: existingCombatant?.initiative ?? token?.initiative ?? 10,
      initiativeRoll: existingCombatant?.initiativeRoll ?? token?.initiativeRoll,
      type: token?.type || existingCombatant?.type || 'player',
      hp,
      maxHp,
      hpObj,
      ac: token?.ac ?? existingCombatant?.ac ?? 15,
      conditions: token?.conditions ?? existingCombatant?.conditions ?? [],
      statusEffects: token?.statusEffects ?? existingCombatant?.statusEffects ?? {},
      statusEffectsMeta: resolvedStatusEffectsMeta,
      concentrationSpell: resolvedConcentrationSpell,
      showKoMarker: token?.showKoMarker ?? existingCombatant?.showKoMarker ?? false
    };
  }, []);

  const getCombatantSkipReason = useCallback((combatant) => (
    getCombatantActionBlockReason(combatant)
  ), []);

  const isCombatantTurnEligible = useCallback((combatant) => (
    !getCombatantSkipReason(combatant)
  ), [getCombatantSkipReason]);

  const findFirstEligibleTurnIndex = useCallback((candidateCombatants) => {
    const list = Array.isArray(candidateCombatants) ? candidateCombatants : [];
    const eligibleIndex = list.findIndex((combatant) => isCombatantTurnEligible(combatant));
    return eligibleIndex >= 0 ? eligibleIndex : (list.length > 0 ? 0 : 0);
  }, [isCombatantTurnEligible]);

  const findNavigatedTurnIndex = useCallback((startIndex, direction, candidateCombatants = combatants) => {
    const list = Array.isArray(candidateCombatants) ? candidateCombatants : [];
    if (list.length === 0) {
      return { index: 0, wrapped: false };
    }

    let index = startIndex;
    let wrapped = false;

    for (let step = 0; step < list.length; step += 1) {
      index = (index + direction + list.length) % list.length;
      if ((direction > 0 && index <= startIndex) || (direction < 0 && index >= startIndex)) {
        wrapped = true;
      }

      if (isCombatantTurnEligible(list[index])) {
        return { index, wrapped };
      }
    }

    return { index: startIndex, wrapped: false };
  }, [combatants, isCombatantTurnEligible]);

  // Update combatants when tokens change - exclude map assets
  // @date_of_change: 2025-10-10 - CRITICAL FIX
  // Once initiative is confirmed (combat started), do NOT rebuild the initiative list from tokens on every token move.
  // Rebuilding was resetting order/indices when a token moved. Instead, merge token changes into existing combatants,
  // preserving initiative values and order. Pre-combat (not confirmed), continue to build/sort from tokens.
  // rule_reference: Initiative stability during combat; movement must not alter turn order.
  const externalTurnNotificationRef = React.useRef('');

  React.useEffect(() => {
    if (authoritativeInitiative || (initiativeExternallyManaged && externalInitiativeState?.combatants)) {
      return;
    }

    if (sourceTokens.length === 0) {
      setCombatants([]);
      return;
    }

    const isLocked = isInitiativeConfirmed || combatStarted;

    if (isLocked) {
      setCombatants(prev => {
        // If we somehow don't have a baseline yet, fall back to initial build
        if (!Array.isArray(prev) || prev.length === 0) {
          const initial = sourceTokens
            .filter(token => token.type !== 'map' && token.type !== 'object')
            .map(token => buildCombatantFromToken(token));
          // Keep existing ordering logic for the very first lock-in
          return initial.sort(compareInitiative);
        }

        // Merge: preserve order and initiatives from prev; update HP/AC/name/type from latest tokens
        const tokenById = new Map(sourceTokens.map(t => [t.id, t]));
        const next = [];
        for (const c of prev) {
          const t = tokenById.get(c.id);
          if (!t || t.type === 'map' || t.type === 'object') {
            // Token removed or not a combatant: drop from list
            continue;
          }
          next.push(buildCombatantFromToken({
            ...t,
            initiative: c.initiative
          }, c));
        }

        // Append any brand-new combatants that were added mid-combat (rare). Keep at end to avoid disturbing order.
        const existingIds = new Set(next.map(c => c.id));
        for (const t of sourceTokens) {
          if (t.type === 'map' || t.type === 'object') continue;
          if (existingIds.has(t.id)) continue;
          next.push(buildCombatantFromToken(t));
        }

        return next;
      });
    } else {
      // Pre-combat: Build and sort fresh from tokens
      const tokenCombatants = sourceTokens
        .filter(token => token.type !== 'map' && token.type !== 'object') // Exclude map assets
        .map(token => buildCombatantFromToken(token))
        .sort(compareInitiative);
      setCombatants(tokenCombatants);
    }
  }, [sourceTokens, isInitiativeConfirmed, combatStarted, buildCombatantFromToken, initiativeExternallyManaged, externalInitiativeState, authoritativeInitiative]);

  React.useEffect(() => {
    if (!initiativeExternallyManaged || !externalInitiativeState?.combatants) {
      return;
    }

    const initiativeData = externalInitiativeState;
    const sourceTokenIds = new Set(sourceTokens.map((token) => token.id));
    const tokenById = new Map(sourceTokens.map((token) => [token.id, token]));
    const updatedCombatants = initiativeData.combatants
      .filter((combatant) => sourceTokenIds.has(combatant.id))
      .map((combatant) => {
        const sourceToken = tokenById.get(combatant.id);
        return sourceToken
          ? buildCombatantFromToken({
              ...sourceToken,
              name: combatant.name || sourceToken.name,
              type: combatant.type || sourceToken.type,
              initiative: combatant.initiative ?? sourceToken.initiative
            }, combatant)
          : combatant;
      });

    setCombatants(updatedCombatants);

    const activeIndex = initiativeData.activeCombatantId
      ? updatedCombatants.findIndex((combatant) => String(combatant.id) === String(initiativeData.activeCombatantId))
      : -1;
    const requestedTurnIndex = activeIndex >= 0
      ? activeIndex
      : (Number.isInteger(Number(initiativeData.currentTurnIndex)) ? Number(initiativeData.currentTurnIndex) : 0);
    const safeTurnIndex = updatedCombatants.length === 0
      ? 0
      : Math.max(0, Math.min(requestedTurnIndex, updatedCombatants.length - 1));

    setCurrentTurnIndex(safeTurnIndex);

    if (initiativeData.round !== undefined) {
      setRound(initiativeData.round);
    }
    if (initiativeData.combatStarted !== undefined) {
      const authoritativeCombatStarted = Boolean(initiativeData.combatStarted);
      setCombatStarted(authoritativeCombatStarted);
      if (!authoritativeCombatStarted) {
        CombatLogManager.synchronizePreCombatState({
          round: initiativeData.round,
          turn: safeTurnIndex
        });
      } else if (!CombatLogManager.combatStarted) {
        CombatLogManager.startCombat(updatedCombatants, viewerVisibleTokens, { authoritativeRecovery: true });
      }
      if (authoritativeCombatStarted) {
        CombatLogManager.synchronizeAuthoritativeTurn({
          round: initiativeData.round,
          turn: safeTurnIndex,
          combatants: updatedCombatants
        });
      }
    }
    if (initiativeData.isInitiativeConfirmed !== undefined) {
      setIsInitiativeConfirmed(Boolean(initiativeData.isInitiativeConfirmed));
    }

    const activeCombatantData = updatedCombatants[safeTurnIndex];
    const externalTurnKey = [
      initiativeData.revision || '',
      activeCombatantData?.id || '',
      safeTurnIndex,
      initiativeData.round ?? '',
      initiativeData.combatStarted ? 'started' : 'pending'
    ].join('::');
    if (activeCombatantData?.id && viewerVisibleTokenIds.has(activeCombatantData.id)) {
      if (externalTurnNotificationRef.current === externalTurnKey) {
        return;
      }
      externalTurnNotificationRef.current = externalTurnKey;

      if (onCombatantChange) {
        onCombatantChange(activeCombatantData);
      }
      if (onTokenSelect) {
        const token = viewerVisibleTokens.find((candidate) => candidate.id === activeCombatantData.id);
        if (token) {
          onTokenSelect(token.id, getTokenLayerId(token));
        }
      }
    }
  }, [
    initiativeExternallyManaged,
    externalInitiativeState,
    sourceTokens,
    viewerVisibleTokens,
    viewerVisibleTokenIds,
    buildCombatantFromToken,
    onCombatantChange,
    onTokenSelect
  ]);

  useEffect(() => {
    if (combatants.length === 0) {
      if (currentTurnIndex !== 0) {
        setCurrentTurnIndex(0);
      }
      return;
    }

    if (currentTurnIndex >= combatants.length) {
      setCurrentTurnIndex(combatants.length - 1);
    }
  }, [combatants.length, currentTurnIndex]);

  // Initialize current combatant on component mount or when the current turn index changes
  // Use a ref to prevent infinite update loops
  const prevCombatantRef = React.useRef();
  
  useEffect(() => {
    if (authoritativeInitiative || initiativeExternallyManaged) return;
    if (!onCombatantChange || combatants.length === 0) return;
    
    // Get current combatant
    const currentCombatant = combatants[currentTurnIndex];
    if (!currentCombatant?.id) return;
    
    // Skip if we've already processed this combatant
    if (prevCombatantRef.current?.id === currentCombatant.id) return;
    
    // Only trigger an update if the combatant has a corresponding token
    if (viewerVisibleTokens.length > 0) {
      // Find if this combatant has a corresponding token
      const tokenExists = viewerVisibleTokenIds.has(currentCombatant.id);
      if (tokenExists) {
        // Update ref to prevent infinite loops
        prevCombatantRef.current = currentCombatant;
        // Only call onCombatantChange if the token exists
        onCombatantChange(currentCombatant);
        
        // Also call onTokenSelect to select the active token if the callback exists
        if (onTokenSelect) {
          const token = viewerVisibleTokens.find((candidate) => candidate.id === currentCombatant.id);
          onTokenSelect(currentCombatant.id, getTokenLayerId(token));
        }
      } else {
        console.log(`[InitiativeTracker] Skipping update for non-existent token ID: ${currentCombatant.id}`);
      }
    }
  }, [authoritativeInitiative, initiativeExternallyManaged, currentTurnIndex, combatants, viewerVisibleTokens, viewerVisibleTokenIds, onCombatantChange, onTokenSelect]);

  // @date_of_change: 2025-10-05 - Listen for WebSocket initiative updates from GM
  // This allows players to see initiative tracker changes in real-time
  // @date_of_change: 2025-10-05 - Listen for WebSocket initiative updates from GM
  // This allows players to see initiative tracker changes in real-time and triggers side effects
  useEffect(() => {
    const handleWebSocketInitiativeUpdate = (event) => {
      const { initiativeData, isRemoteUpdate } = event.detail;

      // Only apply remote updates (from GM or other players), not our own
      if (!isRemoteUpdate) {
        console.log('[InitiativeTracker] Skipping own initiative update');
        return;
      }

      console.log('[InitiativeTracker] Received remote initiative update:', initiativeData);

      // Capture current combat state before updates to detect transitions
      const wasCombatStarted = combatStarted;
      const wasCurrentTurnIndex = currentTurnIndex;

      // Update combatants if provided
      const sourceTokenIds = new Set(sourceTokens.map((token) => token.id));
      const tokenById = new Map(sourceTokens.map((token) => [token.id, token]));
      const updatedCombatants = initiativeData.combatants && Array.isArray(initiativeData.combatants)
        ? initiativeData.combatants
          .filter((combatant) => sourceTokenIds.has(combatant.id))
          .map((combatant) => {
            const sourceToken = tokenById.get(combatant.id);
            if (!sourceToken) return combatant;

            return buildCombatantFromToken({
              ...sourceToken,
              name: combatant.name || sourceToken.name,
              type: combatant.type || sourceToken.type,
              initiative: combatant.initiative ?? sourceToken.initiative
            }, combatant);
          })
        : combatants;
      if (initiativeData.combatants && Array.isArray(initiativeData.combatants)) {
        setCombatants(updatedCombatants);
      }

      // Update current turn index if provided. Prefer the authoritative active
      // combatant id because embedded GMA rooms can filter or rebuild token lists
      // before the numeric index is stable.
      const activeTurnIndex = initiativeData.activeCombatantId
        ? updatedCombatants.findIndex((combatant) => String(combatant.id) === String(initiativeData.activeCombatantId))
        : -1;
      const updatedTurnIndex = activeTurnIndex >= 0
        ? activeTurnIndex
        : (initiativeData.currentTurnIndex !== undefined
          ? initiativeData.currentTurnIndex
          : currentTurnIndex);
      const safeUpdatedTurnIndex = updatedCombatants.length === 0
        ? 0
        : Math.min(updatedTurnIndex, updatedCombatants.length - 1);
      if (initiativeData.currentTurnIndex !== undefined || safeUpdatedTurnIndex !== currentTurnIndex) {
        setCurrentTurnIndex(safeUpdatedTurnIndex);
      }

      // Update round if provided
      if (initiativeData.round !== undefined) {
        setRound(initiativeData.round);
      }

      // Update combat started flag if provided
      const updatedCombatStarted = initiativeData.combatStarted !== undefined
        ? initiativeData.combatStarted
        : combatStarted;
      if (initiativeData.combatStarted !== undefined) {
        setCombatStarted(updatedCombatStarted);
        if (!updatedCombatStarted) {
          CombatLogManager.synchronizePreCombatState({
            round: initiativeData.round,
            turn: safeUpdatedTurnIndex
          });
        }
      }

      if (updatedCombatStarted && !CombatLogManager.combatStarted) {
        CombatLogManager.startCombat(updatedCombatants, viewerVisibleTokens, { authoritativeRecovery: true });
      }
      if (updatedCombatStarted) {
        CombatLogManager.synchronizeAuthoritativeTurn({
          round: initiativeData.round,
          turn: safeUpdatedTurnIndex,
          combatants: updatedCombatants
        });
      }

      // Update initiative confirmed flag if provided
      if (initiativeData.isInitiativeConfirmed !== undefined) {
        setIsInitiativeConfirmed(initiativeData.isInitiativeConfirmed);
      }

      // @date_of_change: 2025-10-05 - Trigger side effects when combat starts remotely
      // Players need CombatLogManager started and current combatant selected
      if (updatedCombatStarted && !wasCombatStarted) {
        // Combat is starting - trigger UI side effects like confirmInitiativeOrder does.
        console.log('[InitiativeTracker] Combat started via WebSocket - CombatLogManager initialized');

        // Notify parent component of current combatant
        if (onCombatantChange && updatedCombatants.length > 0) {
          const currentCombatantData = updatedCombatants[safeUpdatedTurnIndex];
          const tokenExists = currentCombatantData?.id && viewerVisibleTokenIds.has(currentCombatantData.id);
          if (tokenExists) {
            onCombatantChange(currentCombatantData);
            console.log('[InitiativeTracker] Combat start - notified BattleRoom of current combatant:', currentCombatantData.name);
          }
        }

        // Auto-select the active token
        if (onTokenSelect && updatedCombatants.length > 0) {
          const currentCombatantData = updatedCombatants[safeUpdatedTurnIndex];
          const token = viewerVisibleTokens.find(t => t.id === currentCombatantData.id);
          if (token) {
            onTokenSelect(token.id, getTokenLayerId(token));
            console.log('[InitiativeTracker] Combat start - auto-selected token:', currentCombatantData.name);
          }
        }
      }

      // @date_of_change: 2025-10-05 - Trigger side effects when turn changes remotely
      // Players need to see the new active combatant and auto-select their token
      if (updatedCombatStarted && wasCombatStarted && safeUpdatedTurnIndex !== wasCurrentTurnIndex) {
        // Turn changed - update current combatant
        if (onCombatantChange && updatedCombatants.length > 0) {
          const prevCombatant = combatants[wasCurrentTurnIndex];
          const nextCombatant = updatedCombatants[safeUpdatedTurnIndex];
          const tokenExists = nextCombatant?.id && viewerVisibleTokenIds.has(nextCombatant.id);
          if (tokenExists) {
            onCombatantChange(nextCombatant, prevCombatant);
            console.log('[InitiativeTracker] Turn change via WebSocket - notified BattleRoom:', nextCombatant.name);
          }
        }

        // Auto-select the new active token
        if (onTokenSelect && updatedCombatants.length > 0) {
          const nextCombatant = updatedCombatants[safeUpdatedTurnIndex];
          const token = viewerVisibleTokens.find(t => t.id === nextCombatant.id);
          if (token) {
            onTokenSelect(token.id, getTokenLayerId(token));
            console.log('[InitiativeTracker] Turn change via WebSocket - auto-selected token:', nextCombatant.name);
          }
        }
      }
    };

    if (authoritativeInitiative) return undefined;
    window.addEventListener('websocket-initiative-update', handleWebSocketInitiativeUpdate);

    return () => {
      window.removeEventListener('websocket-initiative-update', handleWebSocketInitiativeUpdate);
    };
  }, [authoritativeInitiative, combatStarted, currentTurnIndex, combatants, viewerVisibleTokens, viewerVisibleTokenIds, sourceTokens, onCombatantChange, onTokenSelect, buildCombatantFromToken]);

  const nextTurn = useCallback(() => {
    // Ownership/role gating: GM or controller of active token may advance
    const current = getCurrentCombatant();
    const { canControl: canAdvance } = resolveTurnControl(current);
    if (!canAdvance) {
      console.warn('[InitiativeTracker] Next turn blocked: user does not control the active token');
      return;
    }
    if (!canBroadcast && sendInitiativeUpdate) {
      console.warn('[InitiativeTracker] Next turn proceeding locally (not connected to session)');
      // proceed locally; will broadcast when possible
    }
    // Only allow turn advancement if combat is confirmed
    if (!isInitiativeConfirmed || combatants.length === 0) {
      return;
    }

    if (sendInitiativeUpdate) {
      if (canBroadcast && current) sendInitiativeUpdate({ turnDirection: 'next', sourceTokenId: current.id });
      return;
    }
    const { index: nextIndex, wrapped } = findNavigatedTurnIndex(currentTurnIndex, 1);
    const nextRound = wrapped ? round + 1 : round;
    setCurrentTurnIndex(nextIndex);
    if (wrapped) {
      setRound(nextRound);
      CombatLogManager.startRound(nextRound);
    }
    CombatLogManager.synchronizeAuthoritativeTurn({ round: nextRound, turn: nextIndex, combatants });
    
    // Notify parent component of combatant change
    if (onCombatantChange) {
      const prevCombatant = combatants[currentTurnIndex];
      const nextCombatant = combatants[nextIndex];

      // Find if this combatant has a corresponding token
      const tokenExists = viewerVisibleTokens.some(token => token.id === nextCombatant.id);
      if (tokenExists) {
        onCombatantChange(nextCombatant, prevCombatant);
      } else {
        console.log(`[InitiativeTracker] Skipping update for non-existent token ID: ${nextCombatant.id}`);
      }
    }
    
    // Auto-select the token when advancing turn
    if (onTokenSelect && combatants[nextIndex]) {
      // Find the token by ID and get its layer
      const token = viewerVisibleTokens.find(t => t.id === combatants[nextIndex].id);
      if (token) {
        onTokenSelect(token.id, getTokenLayerId(token));
      }
    }
    
  }, [getCurrentCombatant, resolveTurnControl, canBroadcast, sendInitiativeUpdate, isInitiativeConfirmed, combatants, currentTurnIndex, onCombatantChange, viewerVisibleTokens, onTokenSelect, round, findNavigatedTurnIndex]);

  const prevTurn = useCallback(() => {
    // Ownership/role gating: GM or controller of active token may move turn
    const current = getCurrentCombatant();
    const { canControl: canAdvance } = resolveTurnControl(current);
    if (!canAdvance) {
      console.warn('[InitiativeTracker] Previous turn blocked: user does not control the active token');
      return;
    }
    if (!canBroadcast && sendInitiativeUpdate) {
      console.warn('[InitiativeTracker] Previous turn proceeding locally (not connected to session)');
    }
    // Only allow turn advancement if combat is confirmed
    if (!isInitiativeConfirmed || combatants.length === 0) {
      return;
    }

    if (sendInitiativeUpdate) {
      if (canBroadcast && current) sendInitiativeUpdate({ turnDirection: 'previous', sourceTokenId: current.id });
      return;
    }
    const { index: prevIndex, wrapped } = findNavigatedTurnIndex(currentTurnIndex, -1);
    const prevRound = wrapped ? Math.max(1, round - 1) : round;
    setCurrentTurnIndex(prevIndex);
    if (wrapped) setRound(prevRound);
    CombatLogManager.synchronizeAuthoritativeTurn({ round: prevRound, turn: prevIndex, combatants });
    
    // Notify parent component of combatant change
    if (onCombatantChange) {
      const prevCombatant = combatants[prevIndex];
      // Find if this combatant has a corresponding token
      const tokenExists = viewerVisibleTokens.some(token => token.id === prevCombatant.id);
      if (tokenExists) {
        onCombatantChange(prevCombatant);
      } else {
        console.log(`[InitiativeTracker] Skipping update for non-existent token ID: ${prevCombatant.id}`);
      }
    }
    
    // Auto-select the token when advancing turn
    if (onTokenSelect && combatants[prevIndex]) {
      // Find the token by ID and get its layer
      const token = viewerVisibleTokens.find(t => t.id === combatants[prevIndex].id);
      if (token) {
        onTokenSelect(token.id, getTokenLayerId(token));
      }
    }
    
  }, [getCurrentCombatant, resolveTurnControl, canBroadcast, sendInitiativeUpdate, isInitiativeConfirmed, combatants, currentTurnIndex, onCombatantChange, viewerVisibleTokens, onTokenSelect, round, findNavigatedTurnIndex]);

  // Player-visible: Finish My Turn → logs TURN_END then advances to next turn
  const finishMyTurn = useCallback(() => {
    // Ownership/role gating: GM or controller of active token may finish
    const current = getCurrentCombatant();
    const { canControl: canFinishTurn } = resolveTurnControl(current);
    if (!canFinishTurn) {
      console.warn('[InitiativeTracker] Finish turn blocked: user does not control the active token');
      return;
    }
    if (!canBroadcast && sendInitiativeUpdate) {
      console.warn('[InitiativeTracker] Finish turn proceeding locally (not connected to session)');
    }
    if (!isInitiativeConfirmed || combatants.length === 0) return;

    // current already resolved above
    if (!current) return;
    if (sendInitiativeUpdate) {
      nextTurn();
      return;
    }

    // Gather actions taken this turn from ActionEconomyManager for richer logging
    let actionsTaken = [];
    try {
      actionsTaken = ActionEconomyManager.getTurnActions(current.id) || [];
    } catch (err) {
      actionsTaken = [];
    }

    // Log TURN_END, then advance turn
    try {
      CombatLogManager.endTurn(current, actionsTaken);
    } catch (_) {}

    nextTurn();
  }, [getCurrentCombatant, resolveTurnControl, canBroadcast, sendInitiativeUpdate, isInitiativeConfirmed, combatants.length, nextTurn]);

  /** The same dice preferences apply to first initiative and re-rolls.
   * Cancelling manual input cancels the whole request. date_of_change: 2026-11-04
   */
  const prepareInitiative = useCallback((force) => {
    const rows = combatants.map((actor) => {
      const token = sourceTokens.find((candidate) => candidate.id === actor.id);
      const modifier = initiativeModifierFor(token);
      const reuse = !force && isCurrentInitiativeRoll(actor.initiativeRoll, modifier, actor.initiative);
      return { actor, token, modifier, reuse };
    });
    const manualRolls = [];
    for (const { actor, token, modifier, reuse } of rows) {
      if (reuse || !CombatManager.shouldUseManualRolls(token)) continue;
      const d20 = CombatManager.requestManualNumberSync({
        title: `Manual ${actor.name} initiative`,
        prompt: `Enter raw d20 face for initiative. VCS adds ${modifier >= 0 ? `+${modifier}` : modifier} automatically.`,
        min: 1,
        max: 20,
        fallback: 10
      });
      if (d20 == null) return null;
      manualRolls.push({ combatantId: actor.id, d20 });
    }
    return { rows, manualRolls };
  }, [combatants, sourceTokens]);

  const requestInitiative = useCallback(async (operation) => {
    if (initiativeRequestPending.current) return null;
    initiativeRequestPending.current = true;
    try {
      const prepared = prepareInitiative(operation === 'roll');
      if (!prepared) return null;
      if (sendInitiativeUpdate) {
        // Only the acknowledged, persisted order may start a live room.
        const accepted = await sendInitiativeUpdate({
          combatants: prepared.rows.map(({ actor }) => ({ id: actor.id })),
          initiativeOperation: operation,
          manualRolls: prepared.manualRolls
        });
        if (accepted === false) throw new Error('Reconnect to the room and try again.');
        return null;
      }
      const manualById = new Map(prepared.manualRolls.map(({ combatantId, d20 }) => [combatantId, d20]));
      return prepared.rows.map(({ actor, modifier, reuse }) => {
        if (reuse) return actor;
        const manual = manualById.get(actor.id);
        const roll = buildInitiativeRoll(manual ?? Math.floor(Math.random() * 20) + 1, modifier, manual !== undefined);
        return { ...actor, initiative: roll.total, initiativeRoll: roll };
      }).sort(compareInitiative);
    } catch (error) {
      NotificationManager.error('Initiative not applied', error?.message || 'Review the roster and try again.');
      return null;
    } finally {
      initiativeRequestPending.current = false;
    }
  }, [prepareInitiative, sendInitiativeUpdate]);

  // Confirm initiative order and start combat
  const confirmInitiativeOrder = useCallback(async () => {
    if (!canBroadcast && sendInitiativeUpdate) {
      console.warn('[InitiativeTracker] Confirm blocked: not connected or not in session');
      return;
    }
    const rolledCombatants = await requestInitiative('start');
    if (!rolledCombatants?.length) return;
    setCombatants(rolledCombatants);
    onLocalInitiativeChange?.(rolledCombatants);
    setIsInitiativeConfirmed(true);
    setCombatStarted(true);
    const initialTurnIndex = findFirstEligibleTurnIndex(rolledCombatants);
    setCurrentTurnIndex(initialTurnIndex);

    // @date_of_change: 2025-10-04 - Start combat in CombatLogManager to enable movement tracking
    CombatLogManager.startCombat(rolledCombatants, viewerVisibleTokens);
    CombatLogManager.synchronizeAuthoritativeTurn({ round: 1, turn: initialTurnIndex, combatants: rolledCombatants });
    rolledCombatants.forEach((actor) => CombatLogManager.logEntry(initiativeLogEntry(actor)));
    console.log('[InitiativeTracker] Combat started in CombatLogManager');

    // Notify parent that combat has started
    if (onCombatantChange) {
      const currentCombatant = rolledCombatants[initialTurnIndex];
      // Find if this combatant has a corresponding token
      const tokenExists = viewerVisibleTokens.some(token => token.id === currentCombatant.id);
      if (tokenExists) {
        onCombatantChange(currentCombatant);
      } else {
        console.log(`[InitiativeTracker] Skipping update for non-existent token ID: ${currentCombatant.id}`);
      }
    }

  }, [canBroadcast, sendInitiativeUpdate, requestInitiative, onLocalInitiativeChange, onCombatantChange, viewerVisibleTokens, findFirstEligibleTurnIndex]);

  // Reset initiative order (allow re-rolling)
  const resetInitiativeOrder = useCallback(() => {
    if (!canBroadcast && sendInitiativeUpdate) {
      console.warn('[InitiativeTracker] Reset blocked: not connected or not in session');
      return;
    }
    setIsInitiativeConfirmed(false);
    setCombatStarted(false);
    setCurrentTurnIndex(0);
    setRound(1);
    const resetCombatants = combatants.map(({ initiativeRoll, ...actor }) => ({
      ...actor,
      initiative: initiativeRoll?.modifier ?? actor.initiative
    }));
    setCombatants(resetCombatants);
    if (!sendInitiativeUpdate) onLocalInitiativeChange?.(resetCombatants);

    // @date_of_change: 2025-10-04 - Reset CombatLogManager when resetting initiative
    CombatLogManager.endCombat();
    console.log('[InitiativeTracker] Combat ended in CombatLogManager');

    // @date_of_change 2025-09-15 - Call comprehensive combat reset from BattleRoom
    // This clears actions container and rewinds combat log to beginning as requested
    if (onResetCombat) {
      onResetCombat();
    }

    // @date_of_change: 2025-10-05 - Broadcast combat reset to all players
    if (sendInitiativeUpdate) {
      sendInitiativeUpdate(
        buildInitiativeSyncPayload(
          resetCombatants,
          0,
          1,
          false,
          false
        )
      );
    }
  }, [canBroadcast, sendInitiativeUpdate, buildInitiativeSyncPayload, combatants, onResetCombat, onLocalInitiativeChange]);

  // The compact reset affordance lives beside the ResizablePanel heading. Keep the
  // combat-state mutation here so the tracker remains the single owner of initiative.
  // date_of_change: 2026-09-20
  React.useImperativeHandle(ref, () => ({
    resetCombat: resetInitiativeOrder
  }), [resetInitiativeOrder]);

  // Re-roll initiative for all tokens
  const rerollInitiative = useCallback(async () => {
    if (!canBroadcast && sendInitiativeUpdate) {
      console.warn('[InitiativeTracker] Reroll blocked: not connected or not in session');
      return;
    }
    const rolledCombatants = await requestInitiative('roll');
    if (!rolledCombatants?.length) return;
    setCombatants(rolledCombatants);
    onLocalInitiativeChange?.(rolledCombatants);
    setIsInitiativeConfirmed(false);
    setCombatStarted(false);
    setCurrentTurnIndex(0);
    setRound(1);
  }, [canBroadcast, sendInitiativeUpdate, requestInitiative, onLocalInitiativeChange]);

  const { canControl: playerCanFinishCurrentTurn } = resolveTurnControl(currentCombatant);
  const showPlayerFinishTurn = !isGameMaster
    && isInitiativeConfirmed
    && combatStarted
    && Boolean(currentCombatant?.id)
    && trackerVisibleTokenIds.has(currentCombatant.id)
    && playerCanFinishCurrentTurn;

  return (
    <div className="initiative-tracker">
      {/* Initiative Confirmation Section */}
      {/* date_of_change: 2025-10-11 - Gate GM-only controls (confirm/reroll) for multiplayer fairness
          rule_reference: Only the GM can start or reroll combat initiative in shared sessions. */}
      {!isInitiativeConfirmed && combatants.length > 0 && isGameMaster && !initiativeExternallyManaged && (
        <div className="initiative-confirmation">
          <p className="confirmation-text">Confirm initiative order to start combat:</p>
          <div className="confirmation-buttons">
            <button
              className="confirm-btn"
              onClick={confirmInitiativeOrder}
              title={canBroadcast ? 'Confirm and Start Combat' : 'Connect to session to start combat'}
              disabled={!canBroadcast && !!sendInitiativeUpdate}
            >
              <Check size={15} aria-hidden="true" />
              <span>Confirm and Start Combat</span>
            </button>
            <button 
              className="btn btn-secondary reset-btn"
              onClick={rerollInitiative}
              title={canBroadcast ? 'Re-roll Initiative' : 'Connect to session to reroll'}
              disabled={!canBroadcast && !!sendInitiativeUpdate}
            >
              <Dices size={15} aria-hidden="true" />
              <span>Re-roll Initiative</span>
            </button>
          </div>
        </div>
      )}

      {!isInitiativeConfirmed && combatants.length > 0 && initiativeExternallyManaged && (
        <div className="initiative-confirmation">
          <p className="confirmation-text">Waiting for the player initiative roll in GameMaster Assistant…</p>
        </div>
      )}

      {/* WHAT: Players get one completion action only while they own the active visible turn.
          WHY: The highlighted tracker row already communicates turn identity, while disabled or
          GM-facing Finish controls compete with the authoritative Previous/Next flow.
          date_of_change: 2026-09-20 */}
      {showPlayerFinishTurn && (
        <div className="finish-turn-controls">
          <button
            type="button"
            className="control-btn finish-btn"
            onClick={finishMyTurn}
            title={!canBroadcast && !!sendInitiativeUpdate
              ? 'Finish current turn (will sync when reconnected)'
              : 'Finish current turn'}
          >
            Finish My Turn
          </button>
        </div>
      )}

      <div className="initiative-list-container">
        <ul className="initiative-list">
          {visibleCombatants.map((combatant) => {
            const skipReason = getCombatantSkipReason(combatant);
            const isDowned = Boolean(skipReason);

            return (
              <li
                key={combatant.id}
                className={`initiative-item ${combatant.type} ${
                  combatant.id === currentCombatant?.id ? 'active-turn' : ''
                } ${isDowned ? 'downed' : ''}`}
                aria-current={combatant.id === currentCombatant?.id ? 'step' : undefined}
                onClick={() => {
                  if (onTokenSelect) {
                    // Find the actual token to get its layer
                    const token = viewerVisibleTokens.find(t => t.id === combatant.id);
                    onTokenSelect(combatant.id, getTokenLayerId(token));
                  }
                }}
                title={skipReason || 'Click to select this token on the map'}
              >
                <div className="initiative-header">
                  <span className="initiative-roll">{combatant.initiative}</span>
                  <span className="combatant-name">{combatant.name}</span>
                  {/* GAP-006: Concentration indicator — show spell name when concentrating
                   * rule_reference: D&D 5e 2024 concentration rules (PHB p.234)
                   * date_of_change: 2026-05-20
                   */}
                  {combatant.statusEffects?.concentrating && (
                    <span
                      className="concentration-badge"
                      title={combatant.concentrationSpell ? `Concentrating: ${combatant.concentrationSpell}` : 'Concentrating'}
                      data-testid="concentration-badge"
                    >
                      <Sparkles size={12} aria-hidden="true" />
                      {combatant.concentrationSpell && <span>{combatant.concentrationSpell}</span>}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* GM turn navigation is the only completion control shown to the GM. date_of_change: 2026-09-20 */}
      {isGameMaster && isInitiativeConfirmed && combatStarted && combatants.length > 0 && (
        <div className="turn-controls">
          <button type="button" onClick={prevTurn} className="control-btn prev-btn">
            <ChevronLeft size={15} aria-hidden="true" />
            <span>Previous</span>
          </button>
          <button type="button" onClick={nextTurn} className="control-btn next-btn">
            <span>Next</span>
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
});

InitiativeTracker.displayName = 'InitiativeTracker';

export default InitiativeTracker;
